import { BODY_GROUND_OFFSET } from "game-common/lpc/body_placement"
import { contentSheetResource } from "../content_textures"
import { AnimatedFeedbackParams, Coordinate, Entity, randomIntBetween, TileSize } from "game-common/models"
import { Callback, degrees_to_radians } from "game-common/util"
import { AnimatedSprite, BitmapText, Container, Graphics, Rectangle, Sprite, Text, Texture } from "pixi.js"
import { logger } from "../../../../logger"

import { SpriteMeta } from "../entity_rendering_manager"
import { WorldOverlayItem } from "../world_overlay_surface"
import { BaseRenderable } from "./base_renderable"
import { EntityHighlight, HighlightFrame } from "./entity_highlight"
import { clickAreaFor } from "./click_area"
import { textureContentBounds } from "./texture_content_bounds"
import { TileTextureExtractor } from "./tile_texture_extractor"
import { TextRenderer } from "../text/text_renderer"
import { AnimatedFeedback } from "../effect/animated_feedback"
import { shatterSprite } from "../effects/sprite_shatter"

/**
 * How far below an entity's location its art meets the ground.
 *
 * A location is a sort key, not a foot position. LpcComposite draws a body's 64px frame centred on
 * its container -- so the sole row, 60px down the frame, lands 28px below the centre -- and then
 * lifts the container a tenth of the frame (see LpcComposite.getYOffset). Soles therefore sit
 * 28 - 6.4 px below `location.y`, and that is the line a prop has to touch to look like it is lying
 * on the ground rather than hovering over it.
 */
const GROUND_CONTACT_OFFSET = BODY_GROUND_OFFSET

/**
 * Suffix that asks for the art to be drawn ON the ground: `"<tileset>:<tile>:ground"`.
 *
 * A tileset marker is otherwise drawn AROUND its location, not on it -- the texture is lifted a
 * flat 14px here and the container another quarter of its height in BaseRenderable.getYOffset --
 * which leaves a 32px cell's foot about 11px above the sole line, and more again for art that
 * does not reach the foot of its own cell (an inventory icon, say, with transparent rows under it).
 * That is what makes a prop read as floating. Those offsets are load-bearing for every marker
 * authored against them -- campfires, snares, resource nodes, chests, doors -- so this is opt-in:
 * ask for it and the art's own lowest pixel is placed on the sole line instead, measured from the
 * texture so padding in the source art cannot reintroduce the gap.
 *
 * The suffix survives the `split(":")` above it untouched (the tileset and tile number are the
 * first two segments), so it needs no new field and no change to what the server sends.
 */
const GROUND_ANCHOR_SUFFIX = ":ground"

export class BasicSprite extends BaseRenderable {
    currentColor: number
    animation: AnimatedSprite
    sprite: Sprite
    // SignSprite labels with a BitmapText (bitmap fonts are not Text subclasses), and only
    // `.text`/`.visible`/position are ever touched here, which both types provide.
    labelText: Text | BitmapText

    container: Container
    rotationInterval: any
    private originalColor: number
    private worldOverlayItem: WorldOverlayItem
    private entityHighlight: EntityHighlight
    private clickArea: BaseRenderable
    private _scale: number
    private supportLabel: boolean
    private spriteId: string
    private scaleValue: number
    private spriteMeta: SpriteMeta
    private animatedFeedback: AnimatedFeedback

    // `withClickArea` gives this sprite its own hover/hit surface sized to the rendered art (see
    // updateClickAreaBounds) instead of hit-testing the whole renderable. Entities the player can
    // point at -- the interactable markers -- opt in; pure decoration doesn't.
    constructor(spriteMeta: SpriteMeta, withClickArea?: boolean) {
        super()

        this.spriteMeta = spriteMeta
        const {
            color = 0xffffff,
            name,
            scale = 1.0,
            alpha = 1.0,
            animated,
            animationSpeed = 0.167,
            rotating,
            looping = true,
            rotation,
            onLoopDone,
            supportLabel,
        } = spriteMeta

        this.spriteId = name
        this.supportLabel = supportLabel
        this.scaleValue = scale

        if (withClickArea) {
            this.clickArea = new BaseRenderable()

            if (this.clickArea.parent) {
                this.clickArea.parent.removeChild(this.parentArea)
            }
            // Hover/hit region is sized to the actual rendered art in updateClickAreaBounds()
            // (called below, after the sprite is built). We deliberately draw NOTHING here and
            // rely on pixi's `hitArea` Rectangle instead of a filled Graphics — see that method.
            this.addChild(this.clickArea)
        }
        this.container = new Graphics()
        this.addChild(this.container)

        this.originalColor = color
        this.currentColor = color

        let resource = contentSheetResource("humanoid")

        // A sheet that failed to load leaves the resource present but unparsed, so `.spritesheet`
        // is undefined and reading `.animations` off it throws -- inside a constructor, on the
        // entity-creation path, which takes out every renderable in the room and leaves a world
        // with terrain and no characters in it. One unreachable file should cost one sprite, not
        // all of them, so degrade the way lpc_sprite.setup() already does.
        const sheet = resource?.spritesheet
        const isAnimated = animated !== undefined ? animated : !!sheet?.animations[name]

        this._scale = scale

        // `animated: true` can be passed explicitly, so the frames still have to be there before
        // this branch dereferences them.
        if (isAnimated && sheet?.animations[name]) {
            this.animation = new AnimatedSprite(sheet.animations[name])
            this.animation.scale.set(0.35)

            // set speed, start playback and add it to the stage
            this.animation.animationSpeed = animationSpeed
            this.animation.tint = color
            this.animation.alpha = alpha
            this.animation.play()
            this.animation.scale.set(scale)
            this.container.addChild(this.animation)

            if (!looping) {
                this.animation.loop = false
                if (onLoopDone) {
                    this.animation.onComplete = onLoopDone
                }
            }
        } else {
            let tileIdToUse = name
            let texture
            if (name.includes(":")) {
                const [resourceName, tileId] = name.split(":")
                tileIdToUse = `${resourceName}-${tileId}`
                resource = contentSheetResource(resourceName)

                const tileset = spriteMeta?.tileMapMetaProvider?.getTilesetById(resourceName)

                if (tileset) {
                    const tilesetTileMeta = tileset.tiles[Number(tileId) - 1]
                    if (tilesetTileMeta) {
                        const compositeTileId = tilesetTileMeta.compositeTileId
                        if (compositeTileId) {
                            texture = TileTextureExtractor.instance.extract(
                                spriteMeta.tileMapMetaProvider,
                                tileset.tilesetId,
                                compositeTileId,
                            )
                        }
                    }
                }
            }

            if (!texture) {
                texture = resource?.textures[tileIdToUse]
            }

            if (texture) {
                this.sprite = new Sprite(texture)
                if (name.includes(":")) {
                    this.sprite.transform.position.x -= texture.width / 2
                    this.sprite.transform.position.y -= name.endsWith(GROUND_ANCHOR_SUFFIX)
                        ? groundAnchorLift(texture, name)
                        : 14
                }
                this.sprite.tint = color
                this.sprite.scale.set(scale)
                this.container.addChild(this.sprite)
            }
        }

        this.updateClickAreaBounds()

        this.labelText = new TextRenderer().role("entityLabel").color(0x00ff00).render() as Text
        this.labelText.visible = false
        this.labelText.x = -this.container.width * 0.23
        this.labelText.y = -this.container.height * 0.7
        this.addChild(this.labelText)

        if (rotation !== undefined) {
            this.container.angle = rotation
        }
        if (rotating) {
            this.startRotation()
        }

        this.animatedFeedback = new AnimatedFeedback()
    }

    updateScale = (scale: number) => {
        if (this.animation) {
            this.animation.scale.set(scale)
        }
        if (this.sprite) {
            this.sprite.scale.set(scale)
        }
        this.updateClickAreaBounds()
    }
    startRotation = () => {
        if (this.rotationInterval !== undefined) return
        let angle = this.container.angle
        this.rotationInterval = setInterval(() => {
            if (angle > 360) {
                angle = 0
            }
            this.container.angle = angle
            angle++
        }, 50)
    }

    updateRotating = (rotating: boolean) => {
        if (rotating) {
            this.startRotation()
        } else {
            this.cleanup()
            this.container.angle = 0
        }
    }

    cleanup() {
        if (this.rotationInterval !== undefined) {
            clearInterval(this.rotationInterval)
            this.rotationInterval = undefined
        }
    }

    destroy(options?: Parameters<Container["destroy"]>[0]) {
        this.cleanup()
        super.destroy(options)
    }

    getCurrentColor = () => this.currentColor

    recolor = (color: number) => {
        if (this.currentColor === color) {
            return
        }
        this.currentColor = color
        if (this.animation) {
            this.animation.tint = color
        }
        if (this.sprite) {
            this.sprite.tint = color
        }
    }

    updateName(name: string) {
        if (!this.supportLabel) {
            return
        }
        if (name) {
            this.labelText.text = name.replace(/ /g, "\n")
            this.labelText.visible = true
        } else {
            this.labelText.visible = false
        }
    }

    /**
     * The art a dropped item's discovery glint traces, read fresh every frame (see GlintSubject).
     *
     * Not a copy: a collectable with an expiry spins, so its angle is only right at the moment it
     * is asked for, and its sprite is replaced outright when the item's art changes.
     */
    glintArt = () => (this.sprite ? { sprite: this.sprite, rotation: this.container.rotation } : undefined)

    getClickSurface = (): BaseRenderable => {
        if (this.clickArea) {
            return this.clickArea
        }

        return super.getClickSurface()
    }

    // Size the hover/hit region to the actual rendered art instead of a fixed one-tile box.
    // Uses pixi's `hitArea` Rectangle: nothing is drawn (no batched geometry, no per-frame GPU
    // cost) and hit-testing short-circuits to a rectangle contains-check. This runs ONCE here at
    // construction, and again only on sprite/scale change (both change-gated) — never per frame —
    // so it stays cheap on low-end hardware.
    private updateClickAreaBounds = () => {
        if (!this.clickArea) {
            return
        }

        this.clickArea.x = 0
        this.clickArea.y = 0
        this.clickArea.hitArea = clickAreaFor(this.visibleArtBounds())
    }

    // What this sprite actually draws, in the renderable's own local space -- the region a pointer
    // should have to be inside.
    //
    // NOT simply the container's bounds: a composite tile texture is the bounding box of every tile
    // making up the object (see TileTextureExtractor), and for the ember campfire that box carries
    // ~32px of empty tile out to each side. Taking it at face value made the campfire react to a
    // pointer a whole tile away from the flame. Trim to the texture's opaque pixels where we can;
    // fall back to the container bounds when we can't (an animation, or no renderer to read back
    // from, as in tests).
    private visibleArtBounds = (): Rectangle | null => {
        const containerBounds = this.container.getLocalBounds()
        const fallback =
            containerBounds.width > 0 &&
            containerBounds.height > 0 &&
            isFinite(containerBounds.x) &&
            isFinite(containerBounds.y)
                ? containerBounds
                : null

        if (!this.sprite || this.animation) {
            return fallback
        }
        const content = textureContentBounds(this.sprite.texture, this.spriteId)
        if (!content) {
            return fallback
        }
        // content is in texture pixel space; the sprite's own position already carries the centering
        // shift applied to composite tiles, and its scale the render scale.
        return new Rectangle(
            this.sprite.position.x + content.x * this.sprite.scale.x,
            this.sprite.position.y + content.y * this.sprite.scale.y,
            content.width * this.sprite.scale.x,
            content.height * this.sprite.scale.y,
        )
    }

    // Reports false for a blank stand-in sprite: the V2 chest draws its real art in the TILEMAP and
    // keeps only an invisible 32x32 marker here, so there is no shape here to trace and the caller
    // outlines the map tiles instead. Anything with actual art (a door, an outpost, a dropped item)
    // traces itself.
    setHighlighted = (on: boolean): boolean => {
        if (!this.highlightFrame()) {
            return false
        }
        if (!this.entityHighlight) {
            this.entityHighlight = new EntityHighlight({
                artContainer: this.container,
                frame: this.highlightFrame,
            })
        }
        this.entityHighlight.set(on)
        return true
    }

    private highlightFrame = (): HighlightFrame | null => {
        // A blank stand-in sprite has nothing to outline; say so and take the ring instead.
        if (!this.spriteId || this.spriteId.startsWith("blank")) {
            return null
        }
        const art: any = this.animation ?? this.sprite
        const texture: Texture = this.animation
            ? (this.animation.textures[this.animation.currentFrame] as Texture)
            : this.sprite?.texture
        if (!art || !texture?.valid) {
            return null
        }
        return {
            texture,
            x: art.position.x,
            y: art.position.y,
            scaleX: art.scale.x,
            scaleY: art.scale.y,
            anchorX: art.anchor?.x ?? 0,
            anchorY: art.anchor?.y ?? 0,
        }
    }

    setWorldOverlayItem = (item: WorldOverlayItem) => {
        this.worldOverlayItem = item
    }

    fragment(_: Entity, callback: Callback<void>) {
        const surface = new Graphics()
        surface.scale.set(this._scale)
        surface.rotation = this.container.rotation
        this.parent.addChild(surface)

        const f: Texture = this.animation
            ? (this.animation.textures[this.animation.currentFrame] as any)
            : this.sprite?.texture

        const { width, height } = f._frame

        surface.x -= width / 2
        surface.y -= height / 2

        shatterSprite(surface, f, { onSpawned: () => callback?.(), cacheKey: this.spriteId })
    }

    updateSpriteId(spriteId: string) {
        if (!spriteId) {
            return
        }
        if (this.spriteId === spriteId) {
            // noop, it has changed already
            return
        }

        // todo ... this only really supports non-animated sprites

        if (this.sprite) {
            this.container.removeChild(this.sprite)
        }
        if (this.animation) {
            this.container.removeChild(this.animation)
        }

        this.spriteId = spriteId

        let resource = contentSheetResource("humanoid")
        let tileIdToUse = spriteId
        if (spriteId.includes(":")) {
            const [resourceName, tileId] = spriteId.split(":")
            tileIdToUse = `${resourceName}-${tileId}`
            resource = contentSheetResource(resourceName)
        }

        if (resource) {
            // `resource` being present says only that the loader knows the url, not that the sheet
            // behind it parsed -- a failed or still-parsing sheet leaves `.spritesheet` (or its
            // `.animations`) undefined, and reading through it throws inside this constructor, on
            // the entity-creation path, taking out every renderable in the room. Same reason the
            // constructor above guards; this is the second door into the same trap.
            const isAnimated = !!resource.spritesheet?.animations?.[spriteId]
            if (isAnimated) {
                const {
                    color = 0xffffff,
                    scale = 1.0,
                    alpha = 1.0,
                    animationSpeed = 0.167,
                    looping = true,
                    onLoopDone,
                } = this.spriteMeta
                this.animation = new AnimatedSprite(resource.spritesheet.animations[spriteId])
                this.animation.scale.set(0.35)

                // set speed, start playback and add it to the stage
                this.animation.animationSpeed = animationSpeed
                this.animation.tint = color
                this.animation.alpha = alpha
                this.animation.play()
                this.animation.scale.set(scale)
                this.container.addChild(this.animation)

                if (!looping) {
                    this.animation.loop = false
                    if (onLoopDone) {
                        this.animation.onComplete = onLoopDone
                    }
                }
            } else {
                const texture = resource.textures?.[tileIdToUse]
                if (texture) {
                    this.sprite = new Sprite(texture)
                    this.sprite.tint = this.currentColor
                    this.sprite.scale.set(this.scaleValue)
                    this.container.addChild(this.sprite)
                }
            }
        }

        this.updateClickAreaBounds()
    }

    getYOffset() {
        // Both of these place their art themselves and must not be lifted again: "-exact" is a flat
        // decal drawn from the location down, ":ground" has already been anchored to the sole line.
        if (this.spriteId.includes("-exact") || this.spriteId.endsWith(GROUND_ANCHOR_SUFFIX)) {
            return 0
        }

        return super.getYOffset()
    }

    // Floating feedback over this sprite. A running body's dust trail does NOT come through here --
    // see LpcComposite.feedback and FootDust: it is ground art, z-sorted in the world surface, and
    // EntityRenderingManager.feedback routes it away before it reaches a renderable.
    feedback(entity: Entity, image: string, label: string, params?: AnimatedFeedbackParams) {
        logger.debug({ tags: ["sprite", "feedback"] }, "Feedback")
        this.animatedFeedback.setup(
            0,
            this.worldOverlayItem.height / 2,
            image,
            label,
            this.worldOverlayItem,
            params,
            this.sprite as any,
        )
    }
}

/**
 * How far to raise ground-anchored tile art so its lowest visible pixel lands on the sole line.
 *
 * Measured from the texture rather than assumed from its height, because tile art is not reliably
 * painted down to the foot of its cell. `textureContentBounds` caches per texture and returns null
 * with no renderer (tests, or a sprite built before init), where the cell's own foot is the right
 * answer to fall back to.
 */
const groundAnchorLift = (texture: Texture, cacheKey: string): number => {
    const content = textureContentBounds(texture, cacheKey)
    const foot = content ? content.y + content.height : texture.height
    return foot - GROUND_CONTACT_OFFSET
}
